import { Entity } from '@backstage/catalog-model';
import {
  InfoCard,
  Progress,
  ResponseErrorPanel,
  TableColumn,
} from '@backstage/core-components';
import {
  EntityTable,
  useEntity,
  useRelatedEntities,
} from '@backstage/plugin-catalog-react';
import { Grid } from '@backstage/ui';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { get } from 'lodash';
import { EntityGridItemProps } from './EntityGridItemProps';

type EntityRelatedEntitiesGridItemProps = EntityGridItemProps & {
  title: string;
  relationFilter?: {
    type: string;
    kind?: string;
  };
  columns: TableColumn<Entity>[];
  lgColumns?: TableColumn<Entity>[];
  orderedBy?: string[];
  entityFilter?: (entity: Entity) => boolean;
};

const compareEntitiesByPaths = (
  a: Entity,
  b: Entity,
  paths: string[],
): number => {
  for (const path of paths) {
    const comparison = String(get(a, path, '')).localeCompare(
      String(get(b, path, '')),
    );
    if (comparison !== 0) {
      return comparison;
    }
  }
  return 0;
};

export const EntityRelatedEntitiesGridItem = (
  props: EntityRelatedEntitiesGridItemProps,
) => {
  const { title, relationFilter, columns, lgColumns, orderedBy } = props;
  const { entity } = useEntity();
  const { entities, loading, error } = useRelatedEntities(
    entity,
    relationFilter ?? {},
  );
  const theme = useTheme();
  const isLgOrWider = useMediaQuery(theme.breakpoints.up('lg'));
  if (loading) {
    return (
      <InfoCard variant="gridItem" title={title}>
        <Progress />
      </InfoCard>
    );
  }
  if (error) {
    return (
      <InfoCard variant="gridItem" title={title}>
        <ResponseErrorPanel error={error} />
      </InfoCard>
    );
  }
  const relatedEntities = (entities ?? []).filter(
    props.entityFilter ?? (() => true),
  );
  const sortedEntities = orderedBy
    ? [...relatedEntities].sort((a, b) =>
        compareEntitiesByPaths(a, b, orderedBy),
      )
    : relatedEntities;
  return sortedEntities.length ? (
    <Grid.Item colSpan={props.colSpan}>
      <EntityTable
        variant="gridItem"
        title={title}
        columns={isLgOrWider ? lgColumns ?? columns : columns}
        entities={sortedEntities}
      />
    </Grid.Item>
  ) : null;
};
